import React from 'react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import '../css/components/Pagination.css';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalCount?: number;
  itemsPerPage?: number;
  onPageChange: (page: number) => void;
  loading?: boolean;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalCount,
  itemsPerPage,
  onPageChange,
  loading = false
}) => {
  if (totalPages <= 1) {
    return null;
  }

  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const handlePageClick = (page: number) => {
    if (page !== currentPage) {
      onPageChange(page);
    }
  };

  // Generate page numbers to show
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show current page with context
      let startPage = Math.max(1, currentPage - 2);
      let endPage = Math.min(totalPages, currentPage + 2);
      
      // Adjust if we're at the beginning or end
      if (currentPage <= 3) {
        endPage = Math.min(totalPages, 5);
      } else if (currentPage >= totalPages - 2) {
        startPage = Math.max(1, totalPages - 4);
      }
      
      // Add first page and ellipsis if needed
      if (startPage > 1) {
        pages.push(1);
        if (startPage > 2) {
          pages.push('...');
        }
      }
      
      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      
      // Add ellipsis and last page if needed
      if (endPage < totalPages) {
        if (endPage < totalPages - 1) {
          pages.push('...');
        }
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <div className={`pagination ${loading ? 'loading' : ''}`}>
      <div className="pagination-info">
        {totalCount && itemsPerPage && (
          <span className="results-info">
            Showing {Math.min((currentPage - 1) * itemsPerPage + 1, totalCount)} to{' '}
            {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} results
          </span>
        )}
      </div>
      
      <div className="pagination-controls">
        <button 
          className="pagination-btn prev"
          onClick={handlePrevious}
          disabled={currentPage === 1 || loading}
        >
          <FaChevronLeft />
          Previous
        </button>
        
        <div className="page-numbers">
          {pageNumbers.map((page, index) => (
            <React.Fragment key={index}>
              {page === '...' ? (
                <span className="ellipsis">...</span>
              ) : (
                <button
                  className={`page-btn ${currentPage === page ? 'active' : ''}`}
                  onClick={() => handlePageClick(page as number)}
                  disabled={loading}
                >
                  {page}
                </button>
              )}
            </React.Fragment>
          ))}
        </div>
        
        <button 
          className="pagination-btn next"
          onClick={handleNext}
          disabled={currentPage === totalPages || loading}
        >
          Next
          <FaChevronRight />
        </button>
      </div>
    </div>
  );
};

export default Pagination;